import React, { useState, useEffect, useRef } from 'react';
import PageTitle from '../PageTitle';
import Spacer from '../Spacer';
import '../TutorialsPage/styles.css';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();
const FEEDBACK_DURATION = 45; // 45 seconds

interface PostTutorialFeedbackProps {
  prolificId: string;
  onComplete: () => void;
}

const PostTutorialFeedback: React.FC<PostTutorialFeedbackProps> = ({ prolificId, onComplete }) => {
  const [feedback, setFeedback] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState<number>(FEEDBACK_DURATION);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);
  const [gameStartTime, setGameStartTime] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const transitionRef = useRef<NodeJS.Timeout | null>(null);
  const submittedRef = useRef<boolean>(false);
  const feedbackRef = useRef<string>('');
  const hasTransitionedRef = useRef<boolean>(false);

  // Initialize feedback session and get synchronized timing
  useEffect(() => {
    const initFeedback = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/postTutorialFeedback/init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
        });
        
        if (res.ok) {
          const data = await res.json();
          console.log('[PostTutorialFeedback] Initialized:', data);
          setStartTime(data.startTime);
          setEndTime(data.endTime);
          setGameStartTime(data.gameStartTime);
          setTimeLeft(Math.max(0, Math.floor((data.endTime - Date.now()) / 1000)));
        } else {
          console.error('[PostTutorialFeedback] Failed to initialize');
          // Fallback: use local timing
          const now = Date.now();
          const fallbackEndTime = now + FEEDBACK_DURATION * 1000;
          setStartTime(now);
          setEndTime(fallbackEndTime);
          setGameStartTime(fallbackEndTime);
          setTimeLeft(FEEDBACK_DURATION);
        }
      } catch (err) {
        console.error('[PostTutorialFeedback] Error initializing:', err);
        // Fallback: use local timing
        const now = Date.now();
        const fallbackEndTime = now + FEEDBACK_DURATION * 1000;
        setStartTime(now);
        setEndTime(fallbackEndTime);
        setGameStartTime(fallbackEndTime);
        setTimeLeft(FEEDBACK_DURATION);
      }
    };
    
    initFeedback();
  }, [prolificId]);

  // Keep feedback ref in sync
  useEffect(() => {
    feedbackRef.current = feedback;
  }, [feedback]);

  // Timer countdown and synchronized transition
  useEffect(() => {
    if (!endTime || !gameStartTime) return;
    
    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
      setTimeLeft(remaining);
      
      // Auto-submit when time runs out (if not already submitted)
      if (remaining === 0 && !submittedRef.current) {
        submittedRef.current = true;
        setIsSubmitted(true);
        
        // Submit feedback
        fetch(`${API_BASE_URL}/postTutorialFeedback/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
          body: JSON.stringify({ feedback: feedbackRef.current.trim() }),
        })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) console.log('[PostTutorialFeedback] Auto-submitted:', data);
        })
        .catch(err => console.error('[PostTutorialFeedback] Error auto-submitting:', err));
      }
      
      // Check if it's time to transition to game (synchronized with backend)
      if (now >= gameStartTime && !hasTransitionedRef.current) {
        hasTransitionedRef.current = true;
        console.log('[PostTutorialFeedback] Timer expired - initializing game round and transitioning');
        
        // Initialize first round start time on backend (similar to tutorial round)
        fetch(`${API_BASE_URL}/postTutorialFeedback/initGameRound`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
        })
        .then(res => res.json())
        .then(data => {
          console.log('[PostTutorialFeedback] Game round initialized:', data);
          // Small delay to ensure initialization is processed
          if (transitionRef.current) clearTimeout(transitionRef.current);
          transitionRef.current = setTimeout(() => {
            onComplete();
          }, 200);
        })
        .catch(err => {
          console.error('[PostTutorialFeedback] Error initializing game round:', err);
          // Still transition even if initialization fails
          if (transitionRef.current) clearTimeout(transitionRef.current);
          transitionRef.current = setTimeout(() => {
            onComplete();
          }, 200);
        });
      }
    };
    
    updateTimer();
    timerRef.current = setInterval(updateTimer, 100);
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (transitionRef.current) clearTimeout(transitionRef.current);
    };
  }, [endTime, gameStartTime, prolificId, onComplete]);

  const handleSubmit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setIsSubmitted(true);
    
    try {
      const res = await fetch(`${API_BASE_URL}/postTutorialFeedback/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'prolific-id': prolificId,
        },
        body: JSON.stringify({ feedback: feedback.trim() }),
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('[PostTutorialFeedback] Submitted:', data);
      } else {
        console.error('[PostTutorialFeedback] Failed to submit');
      }
    } catch (err) {
      console.error('[PostTutorialFeedback] Error submitting:', err);
    }
    
    // Don't transition here - let the synchronized timer handle it
    // The transition will happen when gameStartTime is reached
  };

  return (
    <div className="container" style={{ padding: '48px 16px' }}>
      <PageTitle title="Quick Question" />
      <Spacer />
      
      <div style={{
        textAlign: 'center',
        marginBottom: '28px'
      }}>
        <div style={{
          fontSize: '20px',
          fontWeight: 600,
          color: '#0284c7',
          marginBottom: '8px'
        }}>
          {timeLeft} seconds remaining
        </div>
        {timeLeft <= 3 && timeLeft > 0 && (
          <div style={{
            fontSize: '15px',
            color: '#0284c7',
            fontStyle: 'italic'
          }}>
            You will be redirected automatically to the game shortly...
          </div>
        )}
        {timeLeft === 0 && (
          <div style={{
            fontSize: '15px',
            color: '#0284c7',
            fontStyle: 'italic'
          }}>
            Redirecting to the game...
          </div>
        )}
      </div>
      
      <div style={{
        padding: '24px 100px',
        display: 'inline-block',
        textAlign: 'justify',
        textJustify: 'inter-word',
        width: '90vw',
        maxWidth: '800px',
        boxSizing: 'border-box'
      }}>
        <p style={{
          margin: '0 0 20px 0',
          fontSize: '16px',
          lineHeight: '1.7',
          color: '#1e293b'
        }}>
          Please respond to the following question in a few sentences:
        </p>
        
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '2px solid #0ea5e9',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '16px',
            lineHeight: '1.7',
            color: '#0c4a6e',
            fontWeight: 600
          }}>
            How insightful were the game tutorials, and what did you understand about the game mechanics?
          </p>
        </div>
        
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={isSubmitted}
          placeholder="Write your response here..."
          style={{
            width: '100%',
            minHeight: '140px',
            padding: '14px',
            fontSize: '15px',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
            lineHeight: '1.6',
            marginBottom: '20px'
          }}
        />
        
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '8px',
          padding: '14px 18px',
          marginBottom: '24px',
          textAlign: 'left'
        }}>
          <p style={{
            margin: '0',
            fontSize: '15px',
            lineHeight: '1.6',
            color: '#78350f'
          }}>
            <strong style={{ color: '#92400e' }}>Note:</strong> During the game, your discussions in the chat sessions are very important. Since at the end of the game, you will be asked questions that build on your discussions, so please engage actively and thoughtfully in those conversations.
          </p>
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '28px'
        }}>
          <button
            onClick={handleSubmit}
            disabled={isSubmitted || timeLeft === 0}
            style={{
              padding: '14px 40px',
              fontSize: '16px',
              fontWeight: 600,
              color: 'white',
              backgroundColor: isSubmitted || timeLeft === 0 ? '#94a3b8' : '#0284c7',
              border: 'none',
              borderRadius: '8px',
              cursor: isSubmitted || timeLeft === 0 ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              boxShadow: isSubmitted || timeLeft === 0 ? 'none' : '0 2px 4px rgba(2, 132, 199, 0.2)'
            }}
          >
            {isSubmitted ? 'Submitted' : timeLeft === 0 ? 'Time Up' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostTutorialFeedback;
